"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  isLocalPreviewPublicHost,
  publicLocationIdFromPathname,
  readPublicLocationCookie,
  shouldUseRememberedPublicLocationPath,
} from "@/lib/public-location-cookie";

export const browserPublicLocationChangeEventName =
  "tc-public-location-id-change";

export function setBrowserPublicLocationIdOverride(locationId?: string) {
  if (typeof window === "undefined") return;

  if (locationId) {
    document.documentElement.dataset.publicLocationId = locationId;
  } else {
    delete document.documentElement.dataset.publicLocationId;
  }

  window.dispatchEvent(
    new CustomEvent(browserPublicLocationChangeEventName, {
      detail: { locationId },
    }),
  );
}

function browserLocationIdFromPage(initialPublicLocationId?: string) {
  if (typeof window === "undefined") return initialPublicLocationId;

  const pageLocationId = document.documentElement.dataset.publicLocationId;
  if (pageLocationId) {
    return pageLocationId;
  }

  const pathname = window.location.pathname;
  const pathnameLocationId = publicLocationIdFromPathname(pathname);

  if (pathnameLocationId) {
    return pathnameLocationId;
  }

  // Never discard the server-resolved host location: on paths outside the
  // remembered-location set (e.g. /events/[slug] deep links from email), the
  // initial location from the request Host header is the only scope we have,
  // and public queries reject an undefined locationId.
  if (!shouldUseRememberedPublicLocationPath(pathname)) {
    return initialPublicLocationId;
  }

  const host = `${window.location.protocol}//${window.location.host}`;
  if (!isLocalPreviewPublicHost(host)) {
    return initialPublicLocationId;
  }

  return readPublicLocationCookie(document.cookie) ?? initialPublicLocationId;
}

export function useBrowserPublicLocationId(initialPublicLocationId?: string) {
  const pathname = usePathname();
  const [browserPublicLocationId, setCurrentBrowserPublicLocationId] = useState<
    string | undefined
  >(initialPublicLocationId);

  useEffect(() => {
    setCurrentBrowserPublicLocationId(
      browserLocationIdFromPage(initialPublicLocationId),
    );
  }, [initialPublicLocationId, pathname]);

  useEffect(() => {
    const handleLocationChange = (event: Event) => {
      const { locationId } = (
        event as CustomEvent<{ locationId?: string }>
      ).detail ?? {};

      setCurrentBrowserPublicLocationId(locationId);
    };

    window.addEventListener(
      browserPublicLocationChangeEventName,
      handleLocationChange,
    );

    return () => {
      window.removeEventListener(
        browserPublicLocationChangeEventName,
        handleLocationChange,
      );
    };
  }, []);

  return browserPublicLocationId;
}
